import { Box, Typography, Backdrop } from "@mui/material";
import { useState } from "react";
import {
  confirmSignup,
  resendSignupCode,
} from "src/domains/shared/api/signup/signup.repository";
import { signupErrorMessage } from "src/domains/shared/utils/signupErrorMessage";
import {
  TYPOGRAPHY_SCALE,
  COLOR_CONSTANTS,
} from "../shared/constants/styles";
import { TextField } from "../shared/components/TextField";
import { Button } from "../shared/components/Button";
import useLocale from "../shared/hooks/localeHook/useLocale";
import notice from "../shared/utils/notice";
import { useTranslation } from "react-i18next";

interface UserConfirmationBoxProps {
  username: string;
  /** 확정 뒤 이어지는 로그인 — 상자의 「확인」 은 이 약속이 끝날 때까지 진행 중이다 */
  onSuccess: (result: boolean) => Promise<void>;
}

const STYLES = {
  backdrop: {
    backgroundColor: COLOR_CONSTANTS._components.backdrop.fill,
    zIndex: 9998,
  },
  container: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "600px",
    bgcolor: "#ffffff",
    borderRadius: "8px",
    p: "48px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    zIndex: 9999,
  },
  header: {
    ...TYPOGRAPHY_SCALE.h4,
    textAlign: "center",
    mb: "16px",
  },
  label: {
    ...TYPOGRAPHY_SCALE.subtitle2,
    mb: "5px",
  },
  inputWrapper: {
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
  },
  textField: {
    flex: 1,
  },
  button: {
    height: "40px",
    flexShrink: 0,
  },
} as const;

export default function UserConfirmationBox({
  username,
  onSuccess,
}: UserConfirmationBoxProps) {
    const { t } = useTranslation(["auth", "common"]);
  const [code, setCode] = useState("");
  const [locale] = useLocale();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // 백엔드 가입 완료 — 완료면 현행대로 로그인을 이어서 시도하고(끝날 때까지 「확인」 은 진행 중), 거절·재시도는 상자를 유지한 채
  // 알린다(웹 스펙 `signup`).
  const confirm = async () => {
    setIsConfirming(true);
    try {
      const outcome = await confirmSignup({ email: username, code });
      if (outcome.kind === "done") {
        await onSuccess(true);
      } else if (outcome.kind === "retry") {
        notice("error", t("message.signupRetry", { ns: "auth" }));
      } else {
        notice(
          "error",
          signupErrorMessage(outcome.code, locale.locale) ??
            t("auth.message.confirmFailed", { ns: "common" })
        );
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const resendConfirmation = async () => {
    setIsResending(true);
    try {
      const outcome = await resendSignupCode({ email: username });
      if (outcome.kind === "done") {
        notice(
          "success",
          t("auth.message.resendingConfirmationCode", { ns: "common" })
        );
      } else {
        notice(
          "error",
          t("auth.message.confirmationCodeResendingFail", { ns: "common" })
        );
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <Backdrop open={true} sx={STYLES.backdrop} />
      <Box sx={STYLES.container}>
        <Box>
          <Typography sx={STYLES.label}>
            {t("form.verificationCode", { ns: "auth" })}
          </Typography>
          <Box sx={STYLES.inputWrapper}>
            <TextField
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={
                t("myPage.msg.checkVerifyingEmail", { ns: "common" })
              }
              showLabel={false}
              sx={STYLES.textField}
            />
            <Button
              intent="inherit"
              emphasis="outlined"
              size="medium"
              onClick={resendConfirmation}
              disabled={isConfirming || isResending}
              loading={isResending}
              sx={STYLES.button}
            >
              {isResending
                ? t("form.resending", { ns: "auth" })
                : t("constants.resend", { ns: "common" })}
            </Button>
            <Button
              intent="primary"
              emphasis="contained"
              size="medium"
              onClick={confirm}
              disabled={isConfirming || isResending}
              loading={isConfirming}
              sx={STYLES.button}
            >
              {isConfirming
                ? t("form.verifying", { ns: "auth" })
                : t("constants.submit", { ns: "common" })}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}
